import { useCurrentUserQuery } from "@graphql/users/users.g";
import { createBrowserRouter, redirect } from "react-router";

import { queryClient } from "@api/queries";

import { SentryRouteErrorBoundary } from "@components/SentryRouteErrorBoundary/SentryRouteErrorBoundary";

import { CenteredPage } from "@layouts/CenteredPage/CenteredPage.layout";
import { Portal } from "@layouts/Portal/Portal.layout";
import { PortalPage } from "@layouts/PortalPage/PortalPage.layout";

import { dashboardRoutes } from "@pages/dashboard/dashboardRoutes";
import { ResetPasswordPage } from "@pages/login/ResetPassword/ResetPassword.page";
import { loginRoutes } from "@pages/login/loginRoutes";

import { Sentry } from "../sentry";

import { NotFound } from "./NotFound.page";
import { administrationRoutes } from "./administration/administrationRoutes";
import { applicationRoutes } from "./applications/applicationRoutes";
import { clientRoutes } from "./clients/clientRoutes";
import { clientEmployeeRoutes } from "./employees/clientEmployeeRoutes";
import { gettingStartedRoutes } from "./getting-started/gettingStartedRoutes";
import { integrationsRoutes } from "./integrations/integrationsRouter";
import { notificationsRoutes } from "./notifications/notificationsRouter";
import { profileRoutes } from "./profile/profileRouter";
import { routes } from "./routes";
import { userRoutes } from "./users/userRouter";

const sentryCreateBrowserRouter =
  Sentry.wrapCreateBrowserRouter(createBrowserRouter);

export const router = sentryCreateBrowserRouter([
  {
    errorElement: <SentryRouteErrorBoundary />,
    children: [
      {
        path: routes.index.$path(),
        loader: () => redirect("/login"),
      },
      {
        path: routes.authenticate.$path(),
        Component: CenteredPage,
        children: [
          {
            index: true,
            Component: ResetPasswordPage,
          },
        ],
      },
      {
        path: routes.login.$path(),
        Component: CenteredPage,
        loader: async () => {
          try {
            const data = await queryClient.fetchQuery({
              queryKey: useCurrentUserQuery.getKey(),
              queryFn: useCurrentUserQuery.fetcher(),
            });

            if (data.currentUser) {
              return redirect(routes.portal.dashboard.$path());
            }

            return null;
          } catch {
            return null;
          }
        },
        children: loginRoutes,
      },
      {
        path: routes.portal.$path(),
        Component: Portal,
        children: [
          {
            path: routes.portal.gettingStarted.$path(),
            children: gettingStartedRoutes,
          },
          {
            path: routes.portal.dashboard.$path(),
            Component: PortalPage,
            children: dashboardRoutes,
          },
          {
            path: routes.portal.profile.$path(),
            Component: PortalPage,
            children: profileRoutes,
          },
          {
            path: routes.portal.users.$path(),
            Component: PortalPage,
            children: userRoutes,
          },
          {
            path: routes.portal.notifications.$path(),
            Component: PortalPage,
            children: notificationsRoutes,
          },
          {
            path: routes.portal.integrations.$path(),
            Component: PortalPage,
            children: integrationsRoutes,
          },
          {
            path: routes.portal.applications.$path(),
            Component: PortalPage,
            handle: {
              breadcrumb: {
                title: "Applications",
              },
            },
            children: [
              {
                index: true,
                lazy: () =>
                  import("./portal-applications/PortalApplications.page"),
                handle: { fullBleed: true },
              },
            ],
          },
          {
            path: routes.portal.administration.$path(),
            Component: PortalPage,
            handle: {
              breadcrumb: {
                title: "Administration",
              },
            },
            children: administrationRoutes,
          },
          {
            path: routes.portal.clients.$path(),
            Component: PortalPage,
            handle: {
              breadcrumb: {
                title: "Tenants",
              },
            },
            children: clientRoutes,
          },
          {
            path: routes.portal.clients.client.applications.$path(),
            children: applicationRoutes,
          },
          {
            path: routes.portal.clients.client.clientEmployees.$path(),
            children: clientEmployeeRoutes,
          },
        ],
      },
      {
        path: "*",
        Component: CenteredPage,
        children: [
          {
            path: "*",
            Component: NotFound,
          },
        ],
      },
    ],
  },
]);
